import { memo, type ReactNode } from 'react';
import {
  MessageSquare, MonitorUp, PhoneOff, Pause, Play, Settings, Video, VideoOff, Mic, MicOff, MonitorX,
} from 'lucide-react';
import { cn } from '@cyphex/ui';
import { ControlButton } from '@/features/meeting/components/ControlButton';
import { CALL_CHAT_PANEL_ID } from './CallChatPanel';

/**
 * The in-call chat rail. An adversarial review of the first implementation
 * confirmed nine defects; the five that could lose a message, leak one, or burn
 * the user's send budget were fixed first, which is why this is on:
 *
 *   F4  `POST /conversations/:id/members` never recomputed `member_hash`, so a
 *       group that had GAINED a member still resolved from its original
 *       membership — a live chat bug, not a calls one. Recomputed now in the
 *       same transaction, additions and removals alike
 *       (`services/conversationHash.ts`).
 *   F2  The resolver's key sat under `CALL_KEYS.all`, so hang up / transfer /
 *       merge / add people refetched a queryFn whose body is a POST. Moved to
 *       its own `['call-chat', …]` root, and the create is latched per call so
 *       even a whole-cache invalidation only re-reads.
 *   F3  The key was the live roster, so adding somebody mid-call split the
 *       conversation and stranded the draft. One call resolves to one thread,
 *       once.
 *   F5  Any failure on the linked conversation — a 502, a dropped network —
 *       counted as "not mine" and fell through to CREATING a thread. Only a
 *       genuine refusal (403/404) does that now; everything else is an error
 *       state.
 *   F8  The composer's Mic opened a second `getUserMedia` over the call's live
 *       track. Voice messages are suppressed inside the rail.
 *
 * THE REMAINING FOUR ARE NOW FIXED TOO, along with the convergence hole found
 * after that round. None of them could lose or misdirect a message; all four
 * were visible to a user, which is why none was left:
 *
 *   F1  `CallSurface`'s ringing overlay was an `absolute inset-0 z-10` sibling
 *       of the whole room, and `ringing` is true for the ENTIRE outgoing call —
 *       so it painted over the header bar this toolbar now lives in, and Chat
 *       was unreachable for exactly as long as somebody's phone was ringing.
 *       The screen is handed to `CallRoom` as a STAGE overlay instead: over the
 *       video, under the toolbar, beside the rail.
 *   F6  `useCallParties`' `GET /calls/:id` was `retry: false` with no error
 *       branch, so one unlucky request left the rail on skeletons for the rest
 *       of the call. Non-4xx failures are retried twice, and the failure itself
 *       reaches `CallChatPanel`, which offers it with a Try again.
 *   F7  The rail rendered the Composer where `ThreadPane` shows
 *       `ChatRequestBar`, so a guest got a working-looking composer for a
 *       conversation they had not accepted and every send was refused after
 *       retries. `CallChatThread` reads the same `self.requestState` and reuses
 *       the same bar.
 *   F9  Toggling the rail moved `CallStage`'s width by ~320px, so `useTilePlan`
 *       re-diffed and `applySubscriptions` renegotiated on a UI toggle. The
 *       measured width now settles before it is believed, and the tile width is
 *       quantised to the simulcast rung it selects — so a change propagates
 *       only when it would genuinely cross a layer boundary.
 *   F10 The resolved conversation was latched PER CLIENT (a module-scope Map in
 *       `useCallConversation`) and never written back to `cyphex.calls`, so
 *       convergence depended on every party seeing the same roster at the
 *       moment they opened the rail — Alice on a 1:1 with Bob got the DM, and
 *       Bob, opening after Carol was added, got a group. Two people typing into
 *       different threads on one call, reproducible single-client with a
 *       reload. `POST /calls/:id/conversation` now takes the first resolution
 *       and answers with the winner; every client afterwards reads it off
 *       `GET /calls/:id`.
 */
const CALL_CHAT_ENABLED = true;

export interface CallControlsProps {
  /** Transfer / add people / merge / move to meeting, injected by the room. */
  moreMenu?: ReactNode;
  micOn: boolean;
  cameraOn: boolean;
  sharing: boolean;
  onHold: boolean;
  /** The chat rail is open — the button is a disclosure, not a mode. */
  chatOpen: boolean;
  /** Unread messages in the call's thread. 0 hides the badge. */
  chatUnread?: number;
  disabled: boolean;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleShare: () => void;
  onToggleHold: () => void;
  onToggleChat: () => void;
  onOpenDevices: () => void;
  onHangup: () => void;
}

/**
 * The call toolbar. Same glyph-over-label control as the meeting bar, minus
 * everything a two-person call has no use for (hand, reactions, layout, Q&A) —
 * plus hold, which only a call has.
 */
export const CallControls = memo(function CallControls(props: CallControlsProps) {
  return (
    <div
      /* No border or background of its own any more: it sits INSIDE the header
         bar, which already draws both. `justify-end` because it shares that
         bar with the call's identity on the left. */
      className="flex items-center justify-end gap-1"
      role="toolbar"
      aria-label="Call controls"
    >
      <ControlButton
        icon={props.micOn ? Mic : MicOff}
        label={props.micOn ? 'Mute' : 'Unmute'}
        off={!props.micOn}
        disabled={props.disabled}
        onClick={props.onToggleMic}
      />
      <ControlButton
        icon={props.cameraOn ? Video : VideoOff}
        label={props.cameraOn ? 'Stop video' : 'Start video'}
        off={!props.cameraOn}
        disabled={props.disabled}
        onClick={props.onToggleCamera}
      />
      <ControlButton
        icon={props.sharing ? MonitorX : MonitorUp}
        label={props.sharing ? 'Stop share' : 'Share'}
        active={props.sharing}
        disabled={props.disabled}
        onClick={props.onToggleShare}
      />
      <ControlButton
        icon={props.onHold ? Play : Pause}
        label={props.onHold ? 'Resume' : 'Hold'}
        active={props.onHold}
        disabled={props.disabled}
        onClick={props.onToggleHold}
      />
      {CALL_CHAT_ENABLED ? (
        <ControlButton
          icon={MessageSquare}
          label="Chat"
          active={props.chatOpen}
          badgeCount={props.chatUnread ?? 0}
          /* Deliberately NOT gated on `disabled` (= media not connected yet).
             The thread and its history exist before anybody picks up, and
             writing to somebody while their phone rings is half of what this is
             for. */
          aria-expanded={props.chatOpen}
          /* Only while the rail exists: `aria-controls` must name a real node. */
          aria-controls={props.chatOpen ? CALL_CHAT_PANEL_ID : undefined}
          onClick={props.onToggleChat}
        />
      ) : null}
      <ControlButton
        icon={Settings}
        label="Devices"
        disabled={props.disabled}
        onClick={props.onOpenDevices}
      />
      {props.moreMenu}

      <button
        type="button"
        onClick={props.onHangup}
        aria-label="Hang up"
        className={cn(
          'ml-4 flex h-10 items-center gap-2 rounded-pill bg-destructive px-5',
          'text-base font-semibold text-destructive-foreground',
          'transition-colors duration-fast hover:bg-destructive/90',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <PhoneOff size={20} strokeWidth={2} aria-hidden />
        Hang up
      </button>
    </div>
  );
});
